/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// ===============================
// Complimentary Gift Logic
// ===============================

const GIFT_VARIANT = 42954443980896;
const ORIGINAL_PYRITE = 42607167570016;

(async function () {

    async function getCart() {
        const res = await fetch("/cart.js");
        return await res.json();
    }

    async function addGift() {

        const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                items: [{
                    id: GIFT_VARIANT,
                    quantity: 1,
                    properties: {
                        "_gift": "Complimentary Gift"
                    }
                }]
            })
        });

        if (!res.ok) {
            throw new Error("Cart add (gift) failed");
        }

        const data = await res.json();

        document.dispatchEvent(new CustomEvent("cart:updated"));

        return data;

    }

    // -----------------------
    // Change a line item by its cart "key", not by variant id.
    // -----------------------
    // /cart/update.js keys its `updates` payload off variant/line id,
    // which can be unreliable once a theme's Ajax cart has its own
    // notion of line item keys (e.g. items with line-item properties,
    // bundled/duplicate variant lines, etc). /cart/change.js targets
    // one specific line via its unique `key`, which is what the cart
    // is actually rendered from - so removal/quantity changes here
    // are guaranteed to hit the exact line the customer sees.

    async function changeCartItemByKey(key, quantity) {

        const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                id: key,
                quantity
            })
        });

        if (!res.ok) {
            throw new Error("Cart change failed");
        }

        const data = await res.json();

        document.dispatchEvent(new CustomEvent("cart:updated"));

        return data;

    }

    function findGiftLine(cart) {
        return cart.items.find(i => i.variant_id === GIFT_VARIANT);
    }

    // -----------------------
    // Poll helper (Suggestion 4)
    // -----------------------
    // Instead of a blind fixed-length delay after add/remove, poll
    // /cart.js repeatedly until the cart actually reflects the change
    // (or we hit the timeout). This is what removes the need for
    // location.reload() and avoids timing issues on slower networks.

    async function pollCartUntil(predicate, { interval = 300, timeout = 5000 } = {}) {

        const start = Date.now();

        let cart = await getCart();

        while (!predicate(cart) && (Date.now() - start) < timeout) {

            await new Promise(resolve => setTimeout(resolve, interval));

            cart = await getCart();

        }

        return cart;

    }

    function giftQtyIn(cart) {

        const item = cart.items.find(i => i.variant_id === GIFT_VARIANT);

        return item ? item.quantity : 0;

    }

    // Prevents overlapping checkGift() runs - e.g. the interval tick
    // firing again while a previous checkGift() call is still polling
    // the cart for a change to settle.

    let giftCheckRunning = false;

    async function checkGift() {

        if (giftCheckRunning) return;

        giftCheckRunning = true;

        try {

            const cart = await getCart();

            let paidProducts = 0;
            let giftQty = 0;
            let originalQty = 0;

            cart.items.forEach(item => {

                if (item.variant_id === GIFT_VARIANT) {
                    giftQty += item.quantity;
                    return;
                }

                if (item.variant_id === ORIGINAL_PYRITE) {
                    originalQty += item.quantity;
                    return;
                }

                // Count any other line as an eligible paid product.
                // NOTE: this intentionally no longer checks
                // `item.final_price > 0`. Shopify can zero out
                // final_price on a perfectly normal paid product via
                // automatic discounts, coupon codes, or other free-gift
                // promos stacking - that would incorrectly make the
                // customer look ineligible. Eligibility is just "not
                // the gift itself and not the original bracelet".

                paidProducts += item.quantity;

            });

            // Customer purchased original Raw Pyrite
            // Don't give complimentary gift

            if (originalQty > 0 && giftQty > 0) {

                const giftLine = findGiftLine(cart);

                if (giftLine) {
                    await changeCartItemByKey(giftLine.key, 0);
                    await pollCartUntil(c => giftQtyIn(c) === 0);

                    // Force a full refresh so the rendered cart UI
                    // matches Shopify's actual cart state, in case
                    // the theme's own cart-item DOM doesn't react to
                    // the cart:updated event on its own.
                    window.location.reload();
                    return;
                }

                hideGiftControls();

                return;

            }

            // No eligible paid products

            if (paidProducts === 0) {

                if (giftQty > 0) {

                    const giftLine = findGiftLine(cart);

                    if (giftLine) {
                        await changeCartItemByKey(giftLine.key, 0);
                        await pollCartUntil(c => giftQtyIn(c) === 0);

                        window.location.reload();
                        return;
                    }

                    hideGiftControls();

                    return;

                }

            }

            // Eligible paid products exist
            // Gift missing

            else if (giftQty === 0) {

                await addGift();

                await pollCartUntil(c => giftQtyIn(c) > 0);

                window.location.reload();
                return;

            }

            // Prevent duplicate gifts

            if (giftQty > 1) {

                const giftLine = findGiftLine(cart);

                if (giftLine) {
                    await changeCartItemByKey(giftLine.key, 1);
                    await pollCartUntil(c => giftQtyIn(c) <= 1);

                    window.location.reload();
                    return;
                }

                hideGiftControls();

                return;

            }

            hideGiftControls();

        }

        catch (e) {

            console.error("Gift Logic Error:", e);

        }

        finally {

            giftCheckRunning = false;

        }

    }

    // -----------------------
    // Identify gift cart-item rows (Suggestion 3)
    // -----------------------
    // Different Shopify themes render the gift line differently, so
    // we check a few common identifiers instead of relying on just
    // one attribute format. Add more checks here if your theme markup
    // uses something else.

    function isGiftItem(item) {

        const key = item.getAttribute("key") || "";

        if (key.startsWith(GIFT_VARIANT.toString())) return true;

        const dataVariantId =
            item.getAttribute("data-variant-id") ||
            item.getAttribute("data-id") ||
            item.getAttribute("data-line-item-variant-id") ||
            item.getAttribute("data-cart-item-key") ||
            item.getAttribute("data-key");

        if (dataVariantId && dataVariantId.toString().startsWith(GIFT_VARIANT.toString())) return true;

        // Some themes don't expose any data attribute at all and only
        // put the variant/line id in a remove or change link's href
        // (e.g. /cart/change?line=2&id=42954443980896). Fall back to
        // checking for that.

        const link = item.querySelector('a[href*="/cart/change"]');

        if (link) {

            const href = link.getAttribute("href") || "";

            if (href.includes(GIFT_VARIANT.toString())) return true;

        }

        return false;

    }

    function hideGiftControls() {

        document.querySelectorAll(`
            cart-item.cart-item,
            [data-variant-id],
            [data-line-item-variant-id],
            [data-cart-item-key],
            [data-key]
        `).forEach(item => {

            if (!isGiftItem(item)) return;

            // Hide quantity controls

            item.querySelectorAll(`
                quantity-input,
                quantity-display,
                cart-item-quantity,
                .cart-item__qty,
                .quantity,
                .quantity-selector
            `).forEach(el => {

                el.style.display = "none";

            });

            // Hide remove buttons

            item.querySelectorAll(`
                cart-item-remove,
                .cart-item__remove,
                .cart-remove,
                .remove,
                [cart-item-remove]
            `).forEach(el => {

                el.style.display = "none";

            });

            // Add badge once

            if (!item.querySelector(".gift-badge")) {

                const badge = document.createElement("div");

                badge.className = "gift-badge";

                badge.innerHTML = "🎁 <strong>Complimentary Gift</strong>";

                badge.style.marginTop = "10px";
                badge.style.color = "#198754";
                badge.style.fontWeight = "600";
                badge.style.fontSize = "14px";

                const details =
                    item.querySelector(".cart-item__details") ||
                    item.querySelector(".cart-item__qty") ||
                    item;

                details.appendChild(badge);

            }

        });

    }

    // -----------------------
    // Cart sync logic
    // -----------------------

    // Run everywhere so mini cart is also protected

    checkGift();

    // React immediately to the cart-change events most Shopify themes
    // dispatch when the Ajax cart updates. checkGift() is already
    // guarded by giftCheckRunning, so it's safe to attach it to
    // several event names - only whichever ones the theme actually
    // fires (plus our own "cart:updated" dispatches after a
    // successful add/change call) will ever call it, and a call that
    // fires while we're already mid-update just returns immediately.

    function onCartChanged() {

        checkGift();

        hideGiftControls();

    }

    ["cart:refresh", "cart:updated", "cart:update", "cart:change"].forEach(eventName => {

        document.addEventListener(eventName, onCartChanged);

    });

    // Events aren't guaranteed - a theme might use a different event
    // name, or miss dispatching one in some edge case. Keep a slower
    // fallback interval running in the background so the cart still
    // self-corrects even if no event ever fires.

    setInterval(onCartChanged, 5000);

    // -----------------------
    // UI fixes
    // -----------------------

    document.addEventListener("DOMContentLoaded", hideGiftControls);

    setTimeout(hideGiftControls, 300);
    setTimeout(hideGiftControls, 1000);

    // -----------------------
    // Shiprocket Checkout Protection
    // -----------------------

    let checkoutRunning = false;

    // Guard the Shiprocket call so a missing/late-loaded script never
    // leaves the customer stuck with no way to check out at all -
    // fall back to Shopify's own checkout instead.

    function callBuyCart(e) {

        if (
            window.shiprocketCheckoutEvents &&
            typeof shiprocketCheckoutEvents.buyCart === "function"
        ) {

            shiprocketCheckoutEvents.buyCart(e);

        } else {

            window.location.href = "/checkout";

        }

    }

    document.addEventListener("click", async function (e) {

        const btn = e.target.closest(`
            .sr-headless-checkout,
            [name="checkout"],
            button[name="checkout"],
            .cart__checkout,
            .drawer__checkout,
            .drawer-checkout,
            .mini-cart__checkout,
            .sticky-checkout,
            .checkout-btn,
            .buy-now
       `);

        if (!btn || checkoutRunning) return;

        checkoutRunning = true;

        e.preventDefault();
        e.stopImmediatePropagation();

        try {

            // Always fetch latest cart before checkout
            const cart = await getCart();

            let hasGift = false;
            let hasOriginal = false;
            let eligible = false;

            cart.items.forEach(item => {

                if (item.variant_id === GIFT_VARIANT) {

                    hasGift = true;

                } else if (item.variant_id === ORIGINAL_PYRITE) {

                    hasOriginal = true;

                } else {

                    // Same eligibility rule as checkGift(): any other
                    // line counts, regardless of final_price - a
                    // discount/promo zeroing final_price shouldn't
                    // make the customer ineligible here either.

                    eligible = true;

                }

            });

            // ----------------------------
            // Invalid gift (original bracelet purchased)
            // ----------------------------

            if (hasOriginal && hasGift) {

                const giftLine = findGiftLine(cart);

                if (giftLine) {
                    await changeCartItemByKey(giftLine.key, 0);
                    await pollCartUntil(c => giftQtyIn(c) === 0);
                }

            }

            // ----------------------------
            // Gift should not exist
            // ----------------------------

            else if (!eligible && hasGift) {

                const giftLine = findGiftLine(cart);

                if (giftLine) {
                    await changeCartItemByKey(giftLine.key, 0);
                    await pollCartUntil(c => giftQtyIn(c) === 0);
                }

            }

            // ----------------------------
            // Gift missing but eligible
            // ----------------------------

            else if (eligible && !hasOriginal && !hasGift) {

                await addGift();

                await pollCartUntil(c => giftQtyIn(c) > 0);

            }

            hideGiftControls();

            callBuyCart(e);

        } catch (err) {

            console.error("Checkout Gift Error:", err);

            // Never block checkout because of gift logic
            callBuyCart(e);

        } finally {

            // Small delay prevents accidental double-clicks
            setTimeout(() => {

               checkoutRunning = false;

            }, 500);

        }

    }, true);

})();